import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, collectionGroup, doc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../../shared/storage/firebase';
import type { Squad, User } from '../../../../types';
import type { OpsFlow, OpsPipelineStageId } from '../../types';
import { compareOpsFlowPriority, daysAgoShort, dueUrgencyFromDaysToDue, formatDueDelta, type OpsFlowPriority } from '../../utils/priority';
import { ACTIVE_OPS_LOGISTICS_BY_ID as OPS_LOGISTICS_BY_ID } from '../../catalog/activeLogisticsCatalog';
import { OPS_PIPELINE_STAGES, pipelineStageLabel, resolvePipelineStage } from '../../utils/stages';
import { buildCardReadiness, type OpsCardReadiness } from '../../utils/readiness';
import { useAuth } from '../../../auth/contexts/AuthContext';
import UserAvatar from '../../../../shared/ui/UserAvatar';
import ManageSquadUsersModal from '../../../admin/components/ManageSquadUsersModal';
import './FlowsIndexPage.css';

type SortKey = 'priority' | 'due' | 'risk' | 'updated' | 'name';
type DueFilter = 'all' | 'overdue' | 'due_3' | 'due_7' | 'due_14' | 'no_due';
type FlowsView = 'browse' | 'overview';

function safeString(v: any): string {
  return String(v || '').trim();
}

function normalizeSquadLifecycleStatus(v: any): string {
  return String(v || '').trim().toLowerCase();
}

function isInactiveSquadStatus(v: any): boolean {
  const st = normalizeSquadLifecycleStatus(v);
  return st === 'archived' || st === 'canceled' || st === 'cancelled';
}

function ymdFromUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function ymdFromDaysToDueUtc(daysToDue: number): string {
  const now = new Date();
  const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
  const dayMs = 24 * 60 * 60 * 1000;
  const next = new Date(base.getTime() + (Math.round(daysToDue) * dayMs));
  return ymdFromUtcDate(next);
}

function todoSnippet(raw: any, maxLen: number = 90): string {
  const s = String(raw ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '—';
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function enrollmentCountFromSquad(sq: Squad | null | undefined): number {
  if (!sq) return 0;
  const seatsTakenRaw = Number((sq as any)?.seatsTaken);
  const studentIds = Array.isArray((sq as any)?.studentIds) ? ((sq as any).studentIds as any[]) : [];
  const enrolledKidNames = Array.isArray((sq as any)?.enrolledKidNames) ? ((sq as any).enrolledKidNames as any[]) : [];

  const counts = [
    Number.isFinite(seatsTakenRaw) && seatsTakenRaw >= 0 ? Math.trunc(seatsTakenRaw) : 0,
    studentIds.length,
    enrolledKidNames.length,
  ];
  return Math.max(0, ...counts);
}

function parseYmdToUtcMidnight(ymd: string): Date | null {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const dt = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0));
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function addMonthsUtc(d: Date, deltaMonths: number): Date {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  return new Date(Date.UTC(y, m + deltaMonths, 1, 0, 0, 0, 0));
}

function monthLabelUtc(monthStartUtc: Date): string {
  const m = monthStartUtc.getUTCMonth();
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[m] ?? '—'}`;
}

function squadScheduleRangeUtc(sq: Squad | null | undefined): { start: Date; endExclusive: Date } | null {
  if (!sq) return null;
  const startYmd =
    String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.startDate || (sq as any)?.firstDate || '').trim();
  const endYmd =
    String((sq as any)?.schedule?.mainDate || (sq as any)?.schedule?.endDate || (sq as any)?.lastDate || '').trim();

  const start = parseYmdToUtcMidnight(startYmd) || parseYmdToUtcMidnight(endYmd);
  const endInclusive = parseYmdToUtcMidnight(endYmd) || parseYmdToUtcMidnight(startYmd);
  if (!start || !endInclusive) return null;

  // End-exclusive at next UTC midnight to make overlap math easy.
  const endExclusive = new Date(endInclusive.getTime() + 24 * 60 * 60 * 1000);
  return { start, endExclusive };
}

function rangesOverlap(aStart: Date, aEndExclusive: Date, bStart: Date, bEndExclusive: Date): boolean {
  return aStart < bEndExclusive && bStart < aEndExclusive;
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function clamp0to10(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 10) return 10;
  return n;
}

function getValueAtPath(obj: any, fieldPath: string): any {
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

function buildNestedPatchFromPath(fieldPath: string, value: any): any {
  const UNSAFE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  if (parts.length === 0) return {};
  const root: any = Object.create(null);
  let cur: any = root;
  for (let i = 0; i < parts.length; i += 1) {
    const key = parts[i];
    if (!key) continue;
    if (UNSAFE_KEYS.has(key)) throw new Error(`Invalid field path segment: ${key}`);
    if (i === parts.length - 1) {
      cur[key] = value;
      continue;
    }
    const next =
      cur[key] && typeof cur[key] === 'object' && !Array.isArray(cur[key])
        ? cur[key]
        : Object.create(null);
    cur[key] = next;
    cur = next;
  }
  return root;
}

function deepMergeInto(target: any, src: any): any {
  if (!src || typeof src !== 'object' || Array.isArray(src)) return target;
  if (!target || typeof target !== 'object' || Array.isArray(target)) return target;
  for (const k of Object.keys(src)) {
    const sv = src[k];
    const tv = target[k];
    if (sv && typeof sv === 'object' && !Array.isArray(sv) && tv && typeof tv === 'object' && !Array.isArray(tv)) {
      deepMergeInto(tv, sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
}

function expandDottedTopLevelKeys(data: any): any {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const out: any = { ...(data as any) };
  for (const k of Object.keys(data as any)) {
    if (!k.includes('.')) continue;
    try {
      const patch = buildNestedPatchFromPath(k, (data as any)[k]);
      delete out[k];
      deepMergeInto(out, patch);
    } catch {
      // ignore invalid field paths; keep raw data
    }
  }
  return out;
}

function logisticKeyFromId(logisticId: string): string {
  return String(logisticId || '').trim().replace(/\./g, '__');
}

function isLogisticMarkedNotNeeded(flow: OpsFlow, logisticId: string): boolean {
  const id = String(logisticId || '').trim();
  if (!id) return false;
  const key = logisticKeyFromId(id);
  const m: any = (flow as any)?.logisticNotNeededByKey || {};
  return Boolean(m && typeof m === 'object' && m[key]);
}

function isCompleteByRule(def: any, value: any): boolean {
  if (!def) return false;
  if (def.type === 'contact_date') {
    if (!value || typeof value !== 'object') return false;
    const contactId = String((value as any)?.contactId || '').trim();
    const date = String((value as any)?.date || '').trim();
    return Boolean(contactId) && Boolean(date);
  }
  if (def.id === 'squad.firstDayTeacherPacket') {
    const sentAt = (value && typeof value === 'object') ? (value as any).sentAt : null;
    return Number.isFinite(Number(sentAt)) && Number(sentAt) > 0;
  }
  const rule = def.completeness?.kind || 'nonEmpty';
  if (rule === 'exists') {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  }
  if (rule === 'minNumber') {
    if (value === undefined || value === null || value === '') return false;
    const rawMin = (def.completeness as any)?.min;
    const min = Number.isFinite(Number(rawMin)) ? Number(rawMin) : 1;
    return Number(value) >= min;
  }
  // nonEmpty default
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value === true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
}

function daysToDueUtc(ymd: string, now: Date): number | null {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const due = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0));
  if (Number.isNaN(due.getTime())) return null;
  const dayMs = 24 * 60 * 60 * 1000;
  const nowMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
  return Math.round((due.getTime() - nowMidnight.getTime()) / dayMs);
}

function scoreOpsFlowPriorityFromSsot(params: {
  flow: OpsFlow;
  partner: any;
  location: any;
  partnerSeason: any;
  squad: Squad | null | undefined;
  derivedOkByRuleId: Record<string, boolean> | null | undefined;
  now: Date;
}): OpsFlowPriority {
  const { flow, partner, location, partnerSeason, squad, derivedOkByRuleId, now } = params;
  const risk = clamp0to10(Number((flow as any)?.riskScoreOutOf10 ?? 0));

  // Build checkpoint defs from applied snapshots.
  const defsById = new Map<string, any>();
  const sources = Array.isArray((flow as any)?.appliedSources) ? ((flow as any).appliedSources as any[]) : [];
  for (const src of sources) {
    const snapshot = (src as any)?.snapshot || null;
    const cps = snapshot && Array.isArray((snapshot as any)?.checkpoints) ? ((snapshot as any).checkpoints as any[]) : [];
    for (const cp of cps) {
      const id = String(cp?.checkpointId || '').trim();
      if (!id) continue;
      defsById.set(id, cp);
    }
  }

  const orderedCpIds = Array.from(defsById.keys()).sort((a, b) => {
    const ao = Number((flow as any)?.checkpointState?.[a]?.orderOverride ?? defsById.get(a)?.order ?? 0);
    const bo = Number((flow as any)?.checkpointState?.[b]?.orderOverride ?? defsById.get(b)?.order ?? 0);
    if (ao !== bo) return ao - bo;
    return String(defsById.get(a)?.title || a).localeCompare(String(defsById.get(b)?.title || b));
  });

  // Overall completion across all tasks via SSOT logistic values.
  let overallTotal = 0;
  let overallOk = 0;
  const completionForTask = (task: any): boolean | null => {
    const logisticId = String(task?.logisticId || '').trim();
    if (!logisticId) return null;
    const def: any = (OPS_LOGISTICS_BY_ID as any)[logisticId] || null;
    if (!def) return null;

    // "Not needed" / "removed" is a per-flow override (counts as complete).
    const notNeeded = isLogisticMarkedNotNeeded(flow, logisticId);
    if (notNeeded) return true;

    // Derived logistics (contacts/billing/enrollment) — use derivedOk when available.
    if (def.scope === 'derived' && def.storage?.kind === 'derived') {
      const ruleId = String(def.storage.ruleId || '').trim();
      if (!ruleId) return false;
      // squad-derived: enrollment meets min
      if (ruleId === 'enrollmentMeetsMin') {
        const minEnroll = Number((squad as any)?.minEnroll);
        const seatsTaken = Number((squad as any)?.seatsTaken);
        const fallbackEnrollment = Array.isArray((squad as any)?.studentIds) ? ((squad as any).studentIds as any[]).length : 0;
        const enrollment = Number.isFinite(seatsTaken)
          ? Math.max(Math.max(0, Math.trunc(seatsTaken)), Math.max(0, Math.trunc(fallbackEnrollment)))
          : fallbackEnrollment;
        return Number.isFinite(minEnroll) && minEnroll > 0 ? enrollment >= minEnroll : false;
      }
      // Default: look up canonical partner/contact-derived flag.
      return Boolean((derivedOkByRuleId || {})[ruleId]);
    }

    let value: any = undefined;
    if (def.storage?.kind === 'partners') value = getValueAtPath(partner as any, def.storage.fieldPath);
    else if (def.storage?.kind === 'locations') value = getValueAtPath(location as any, def.storage.fieldPath);
    else if (def.storage?.kind === 'partner_seasons') value = getValueAtPath(partnerSeason as any, def.storage.fieldPath);
    else if (def.storage?.kind === 'squads') value = getValueAtPath(squad as any, def.storage.fieldPath);
    return isCompleteByRule(def, value);
  };

  const checkpointPercent = (cpDef: any): number => {
    const tasks = Array.isArray(cpDef?.tasks) ? (cpDef.tasks as any[]) : [];
    const relevant = tasks.filter((t) => {
      const tid = String(t?.taskId || '').trim();
      if (!tid) return false;
      const st = String((flow as any)?.taskState?.[tid]?.status || 'open');
      if (st === 'superseded') return false;
      return String(t?.logisticId || '').trim() !== '';
    });
    if (relevant.length === 0) return 100;

    let ok = 0;
    for (const t of relevant) {
      const complete = completionForTask(t);
      if (complete === true) ok += 1;
    }
    return Math.round((ok / relevant.length) * 100);
  };

  // Compute overall completion across all unique tasks (by id) once.
  const allTasksById = new Map<string, any>();
  for (const cpId of orderedCpIds) {
    const cpDef = defsById.get(cpId);
    const tasks = Array.isArray(cpDef?.tasks) ? (cpDef.tasks as any[]) : [];
    for (const t of tasks) {
      const tid = String(t?.taskId || '').trim();
      if (!tid) continue;
      if (allTasksById.has(tid)) continue;
      const st = String((flow as any)?.taskState?.[tid]?.status || 'open');
      if (st === 'superseded') continue;
      if (!String(t?.logisticId || '').trim()) continue;
      allTasksById.set(tid, t);
    }
  }
  for (const t of allTasksById.values()) {
    const complete = completionForTask(t);
    if (complete === null) continue;
    overallTotal += 1;
    if (complete) overallOk += 1;
  }

  let currentCheckpoint: OpsFlowPriority['currentCheckpoint'] = null;
  for (const id of orderedCpIds) {
    const st = String((flow as any)?.checkpointState?.[id]?.status || '');
    if (st === 'superseded') continue;
    const def = defsById.get(id);
    const pct = checkpointPercent(def);
    if (pct < 100) {
      const title = String(def?.title || id).trim() || id;
      const dueDateYmd = String((flow as any)?.checkpointState?.[id]?.dueDate || '').trim() || undefined;
      const daysToDue = dueDateYmd ? daysToDueUtc(dueDateYmd, now) : null;
      currentCheckpoint = { checkpointId: id, title, percent: pct, dueDateYmd, daysToDue };
      break;
    }
  }

  const overallPercentComplete = overallTotal === 0 ? 0 : Math.round((overallOk / overallTotal) * 100);
  // Prefer the Flow-level TODO due date when present (this is the "total TODO" due date),
  // falling back to the active checkpoint due date for legacy flows.
  const hasFlowTodoField = Object.prototype.hasOwnProperty.call((flow as any) || {}, 'flowTodo');
  const ft: any = hasFlowTodoField ? ((flow as any)?.flowTodo || {}) : null;
  const flowTodoDueYmd = hasFlowTodoField ? String(ft?.dueDate || '').slice(0, 10) : '';
  const flowTodoDaysToDue = flowTodoDueYmd ? daysToDueUtc(flowTodoDueYmd, now) : null;
  const effectiveDaysToDue = hasFlowTodoField ? flowTodoDaysToDue : (currentCheckpoint?.daysToDue ?? null);

  const { dueUrgency, needsDueDate } = dueUrgencyFromDaysToDue(effectiveDaysToDue);
  const priorityScore = (0.55 * dueUrgency + 0.45 * risk) * 10;

  return {
    flowId: String((flow as any)?.id || (flow as any)?.squadId || ''),
    squadId: String((flow as any)?.squadId || (flow as any)?.id || ''),
    risk,
    dueUrgency,
    needsDueDate,
    priorityScore,
    currentCheckpoint,
    overallPercentComplete,
  };
}

function dueBadgeTone(daysToDue: number | null): 'danger' | 'warn' | 'muted' {
  if (daysToDue === null) return 'muted';
  if (daysToDue < 0) return 'danger';
  if (daysToDue <= 7) return 'warn';
  return 'muted';
}

const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
const US_STATE_CODES = new Set(Object.keys(US_STATES));
const US_STATE_NAME_ENTRIES: Array<[string, string]> = Object.entries(US_STATES).map(([code, name]) => [code, name.toLowerCase()]);

function usStateFromAddress(address?: string): string {
  const s = String(address || '').trim();
  if (!s) return '';
  const up = s.toUpperCase();
  // "..., IL 60601" or "..., IL"
  const trailing = up.match(/\b([A-Z]{2})\b(?:\s+\d{5}(?:-\d{4})?)?\s*$/);
  if (trailing && US_STATE_CODES.has(trailing[1])) return trailing[1];
  const afterComma = up.match(/,\s*([A-Z]{2})\b/);
  if (afterComma && US_STATE_CODES.has(afterComma[1])) return afterComma[1];
  for (const [code, name] of US_STATE_NAME_ENTRIES) {
    if (new RegExp(`\\b${name}\\b`, 'i').test(s)) return code;
  }
  return '';
}

function normalizeStateToCode(raw?: string): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const up = s.toUpperCase();
  if (s.length === 2 && US_STATE_CODES.has(up)) return up;
  const lower = s.toLowerCase();
  for (const [code, name] of US_STATE_NAME_ENTRIES) if (name === lower) return code;
  return usStateFromAddress(s);
}

export default function FlowsIndexPage(): React.ReactElement {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [flows, setFlows] = useState<OpsFlow[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [partnerSeasons, setPartnerSeasons] = useState<any[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [derivedOkByPartnerId, setDerivedOkByPartnerId] = useState<Record<string, Record<string, boolean>>>({});
  const [billingDefaultsByPartnerId, setBillingDefaultsByPartnerId] = useState<Record<string, any>>({});
  const [queryText, setQueryText] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('priority');
  const [dueFilter, setDueFilter] = useState<DueFilter>('all');
  const [riskMin, setRiskMin] = useState<number>(0);
  const [riskMax, setRiskMax] = useState<number>(10);
  const [cpPctMin, setCpPctMin] = useState<number>(0);
  const [cpPctMax, setCpPctMax] = useState<number>(100);
  const [overallPctMin, setOverallPctMin] = useState<number>(0);
  const [overallPctMax, setOverallPctMax] = useState<number>(100);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all'); // all | unassigned | <userId>
  const [assigneePicker, setAssigneePicker] = useState<null | { flowId: string; anchorRect: { top: number; left: number; right: number; bottom: number } }>(null);
  const [view, setView] = useState<FlowsView>('browse');
  const [draggingFlowId, setDraggingFlowId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<OpsPipelineStageId | null>(null);
  const [savingStageFlowId, setSavingStageFlowId] = useState<string | null>(null);
  const [expandedCardIds, setExpandedCardIds] = useState<Record<string, boolean>>({});

  // Overview board filters (independent from the Browse tab filters).
  const [boardSearch, setBoardSearch] = useState('');
  const [boardAssignee, setBoardAssignee] = useState<string>('all'); // all | unassigned | <userId>
  const [boardDue, setBoardDue] = useState<DueFilter>('all');
  const [boardPriority, setBoardPriority] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [boardFlags, setBoardFlags] = useState<'all' | 'attention' | 'no_due'>('all');
  const [boardLocation, setBoardLocation] = useState<string>('all'); // all | <stateCode> | unknown

  const [todoEditor, setTodoEditor] = useState<null | { flowId: string; draft: string; initial: string }>(null);
  const [dueEditor, setDueEditor] = useState<null | { flowId: string; draftYmd: string; initialYmd: string }>(null);
  const [manageStudentsSquadId, setManageStudentsSquadId] = useState<string | null>(null);
  const [savingTodoFlowId, setSavingTodoFlowId] = useState<string | null>(null);
  const [savingDueFlowId, setSavingDueFlowId] = useState<string | null>(null);
  const todoTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const dueInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'partnerSeasonFlows')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), id: d.id } as OpsFlow));
      list.sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
      setFlows(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('isAdmin', '==', true)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as User));
        list.sort((a, b) => String(a.displayName || a.email || '').localeCompare(String(b.displayName || b.email || '')));
        setAdminUsers(list);
      },
      () => setAdminUsers([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'squads')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), id: d.id } as Squad));
      list.sort((a, b) => String((a as any)?.title || '').localeCompare(String((b as any)?.title || '')));
      setSquads(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => onSnapshot(collection(db, 'partners'), (snap) => {
    setPartners(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
  }, () => setPartners([])), []);

  useEffect(() => onSnapshot(collection(db, 'locations'), (snap) => {
    setLocations(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
  }, () => setLocations([])), []);

  useEffect(() => onSnapshot(collectionGroup(db, 'seasons'), (snap) => {
    setPartnerSeasons(snap.docs.map((item) => ({
      id: item.id,
      partnerId: item.ref.parent.parent?.id || '',
      ...(item.data() as any),
    })));
  }, () => setPartnerSeasons([])), []);

  const partnerById = useMemo(
    () => new Map(partners.map((partner) => [String(partner.id), partner])),
    [partners],
  );
  const locationById = useMemo(
    () => new Map(locations.map((location) => [String(location.id), location])),
    [locations],
  );
  const partnerSeasonById = useMemo(
    () => new Map(partnerSeasons.map((season) => [
      `${String(season.partnerId)}__${String(season.id)}`,
      season,
    ])),
    [partnerSeasons],
  );

  const squadById = useMemo(() => {
    const m = new Map<string, Squad>();
    squads.forEach(s => m.set(String((s as any).id), s));
    return m;
  }, [squads]);

  // Compute derived completion flags from canonical partner contacts/billing.
  // This is necessary because some checkpoints include derived logistics (contacts, billing profile, etc).
  useEffect(() => {
    const partnerIds = Array.from(new Set(flows.map((flow) => String(flow.partnerId || '').trim()).filter(Boolean)));
    if (partnerIds.length === 0) return;

    let alive = true;
    const run = async () => {
      const okByPartner: Record<string, Record<string, boolean>> = {};
      const billingByPartner: Record<string, any> = {};

      await Promise.all(partnerIds.map(async (partnerId) => {
        const ok: Record<string, boolean> = {};
        const partner = partnerById.get(partnerId) || null;
        const billingDefaults = expandDottedTopLevelKeys(partner?.billingDefaults || {}) || {};
        billingByPartner[partnerId] = billingDefaults;
        const authorizedIds = Array.isArray(billingDefaults.authorizedContactIds)
          ? billingDefaults.authorizedContactIds.map(String).filter(Boolean)
          : [];
        ok.billingProfileExists = Object.keys(billingDefaults).length > 0;
        ok.approvedBillingRecipientExists = authorizedIds.length > 0;

        try {
          const contactsSnap = await getDocs(collection(db, 'partners', partnerId, 'contacts'));
          const flowLocationIds = new Set(
            flows.filter((flow) => flow.partnerId === partnerId)
              .flatMap((flow) => Array.isArray(flow.locationIds) ? flow.locationIds : [])
              .map(String)
          );
          const contacts = contactsSnap.docs.map((item) => item.data() as any).filter((contact) => {
            const ids = Array.isArray(contact.locationIds) ? contact.locationIds.map(String).filter(Boolean) : [];
            return ids.length === 0 || ids.some((id: string) => flowLocationIds.has(id));
          });
          const hasRoleOrTag = (role: string) => contacts.some((contact) =>
            String(contact.role || '') === role
            || (Array.isArray(contact.tags) && contact.tags.map(String).includes(role))
          );
          ok.financeContactExists = hasRoleOrTag('finance');
          ok.schoolDecisionMakerExists = contacts.some((contact) => contact.isDecisionMaker === true);
          ok.schoolPrimaryContactExists = contacts.some((contact) => contact.isPrimaryContact === true);
          ok.schoolAfterschoolDirectorExists = hasRoleOrTag('afterschool_director');
          ok.schoolTeacherChampionExists = Boolean(partner?.teacherChampionOptional) || hasRoleOrTag('teacher_champion');
        } catch {
          ok.financeContactExists = false;
          ok.schoolDecisionMakerExists = false;
          ok.schoolPrimaryContactExists = false;
          ok.schoolAfterschoolDirectorExists = false;
          ok.schoolTeacherChampionExists = Boolean(partner?.teacherChampionOptional);
        }

        okByPartner[partnerId] = ok;
      }));

      if (!alive) return;
      setDerivedOkByPartnerId(okByPartner);
      setBillingDefaultsByPartnerId(billingByPartner);
    };
    void run();

    return () => { alive = false; };
  }, [flows, partnerById]);

  const now = useMemo(() => new Date(), []);
  const adminUserById = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of adminUsers) m.set(String((u as any)?.id || ''), u);
    return m;
  }, [adminUsers]);

  type FlowVM = {
    flow: OpsFlow;
    flowId: string;
    squadId: string;
    squadTitle: string;
    squadStatus: string;
    studentCount: number;
    scheduleRangeUtc: { start: Date; endExclusive: Date } | null;
    partnerId: string;
    partnerName: string;
    locationNames: string[];
    updatedAt: number;
    priority: OpsFlowPriority;
    hasCurrentCheckpoint: boolean;
    cpTitle: string;
    cpTodo: string;
    cpTodoShort: string;
    todo: string;
    todoShort: string;
    ownerUserId: string;
    ownerUser: User | null;
    cpPercent: number;
    cpDaysToDue: number | null;
    cpDueLabel: string;
    todoDaysToDue: number | null;
    todoDueLabel: string;
    needsDueDate: boolean;
    pipelineStage: OpsPipelineStageId;
    readiness: OpsCardReadiness;
    readinessReady: boolean;
    locationState: string;
  };

  const vms: FlowVM[] = useMemo(() => {
    const list: FlowVM[] = [];
    for (const f of flows) {
      const squadId = String((f as any)?.squadId || (f as any)?.squadIds?.[0] || '').trim();
      const sq = squadById.get(squadId);
      if (sq && isInactiveSquadStatus((sq as any)?.status)) {
        continue;
      }
      const partnerId = String((f as any)?.partnerId || (sq as any)?.partnerId || '').trim();
      const partner = partnerById.get(partnerId);
      const flowLocations = (Array.isArray(f.locationIds) ? f.locationIds : [])
        .map((locationId) => locationById.get(String(locationId)))
        .filter(Boolean);
      const locationNames = flowLocations.map((location) => String(location?.name || '')).filter(Boolean);
      const squadTitle = String((sq as any)?.title || squadId || 'Squad').trim() || 'Squad';
      const partnerName = String((partner as any)?.name || partnerId || '—').trim() || '—';
      const squadStatus = String((sq as any)?.status || '').trim();
      const studentCount = enrollmentCountFromSquad(sq || null);
      const scheduleRangeUtc = squadScheduleRangeUtc(sq || null);

      const pr = scoreOpsFlowPriorityFromSsot({
        flow: { ...(f as any), id: String((f as any).id || squadId), squadId } as OpsFlow,
        partner: partner || null,
        location: flowLocations[0] || null,
        partnerSeason: partnerSeasonById.get(`${partnerId}__${String((f as any)?.seasonId || '')}`) || null,
        squad: sq || null,
        derivedOkByRuleId: derivedOkByPartnerId[partnerId] || {},
        now,
      });
      const cp = pr.currentCheckpoint;
      const cpTitle = cp ? String(cp.title || cp.checkpointId) : '—';
      const cpId = cp ? String((cp as any)?.checkpointId || '').trim() : '';
      const cpTodo = cpId ? String(((f as any)?.checkpointState || {})[cpId]?.note || '') : '';
      const cpTodoShort = todoSnippet(cpTodo, 110);
      const cpPercent = cp ? clamp(Number(cp.percent), 0, 100) : 100;
      const cpDaysToDue = cp ? cp.daysToDue : null;
      const needsDueDate = Boolean(pr.needsDueDate);
      const cpDueLabel = cp ? formatDueDelta(cpDaysToDue) : 'No active checkpoint';
      const ownerUserId = String((f as any)?.internalOwnerUserId || '').trim();
      const ownerUser = ownerUserId ? (adminUserById.get(ownerUserId) || null) : null;

      // Use the Flow-level TODO due date when present; otherwise fall back to checkpoint due.
      const hasFlowTodoField = Object.prototype.hasOwnProperty.call((f as any) || {}, 'flowTodo');
      const ft: any = hasFlowTodoField ? ((f as any)?.flowTodo || {}) : null;
      const flowTodoDueYmd = hasFlowTodoField ? String(ft?.dueDate || '').slice(0, 10) : '';
      const flowTodoDaysToDue = flowTodoDueYmd ? daysToDueUtc(flowTodoDueYmd, now) : null;
      const todoDaysToDue = hasFlowTodoField ? flowTodoDaysToDue : cpDaysToDue;
      const todoDueLabel = hasFlowTodoField
        ? (flowTodoDueYmd ? formatDueDelta(todoDaysToDue) : 'No due date')
        : cpDueLabel;

      // Use the Flow-level TODO note when present; otherwise fall back to the current checkpoint note.
      const flowTodoNote = hasFlowTodoField ? String(ft?.note || '') : '';
      const todo = hasFlowTodoField ? flowTodoNote : cpTodo;
      const todoShort = todoSnippet(todo, 110);

      // Manual pipeline stage wins; otherwise infer from legacy data on read.
      const pipelineStage = resolvePipelineStage(f as OpsFlow, {
        squadStatus,
        hasCurrentCheckpoint: Boolean(cp),
        overallPercentComplete: pr.overallPercentComplete,
        allCheckpointsComplete: Boolean((pr as any)?.allCheckpointsComplete),
        currentCheckpointTitle: cp ? cpTitle : '',
      });

      // Location (US state): prefer the structured billing address state, then
      // fall back to linked location addresses.
      const billingState = String(((billingDefaultsByPartnerId[partnerId] || {}) as any)?.billingAddress?.state || '').trim();
      const locationState = normalizeStateToCode(billingState)
        || flowLocations.map((location) => usStateFromAddress(String(location?.address || ''))).find(Boolean)
        || '';

      // Readiness badges come from SSOT logistics. Billing/derived data loads
      // async per partner, so only trust gate-derived readiness once it is in.
      const readinessReady = Object.prototype.hasOwnProperty.call(derivedOkByPartnerId, partnerId);
      const readiness = buildCardReadiness(
        { ...(f as any), id: String((f as any).id || squadId), squadId } as OpsFlow,
        {
          partner: partner || null,
          location: flowLocations[0] || null,
          partnerSeason: f,
          squad: sq || null,
          billingProfile: {},
          partnerBillingProfile: {},
          derivedOkByRuleId: derivedOkByPartnerId[partnerId] || {},
        },
        { needsDueDate },
      );

      list.push({
        flow: f,
        flowId: String((f as any)?.id || squadId),
        squadId,
        squadTitle,
        squadStatus,
        studentCount,
        scheduleRangeUtc,
        partnerId,
        partnerName,
        locationNames,
        updatedAt: Number((f as any)?.updatedAt || 0),
        priority: pr,
        hasCurrentCheckpoint: Boolean(cp),
        cpTitle,
        cpTodo,
        cpTodoShort,
        todo,
        todoShort,
        ownerUserId,
        ownerUser,
        cpPercent,
        cpDaysToDue,
        cpDueLabel,
        todoDaysToDue,
        todoDueLabel,
        needsDueDate,
        pipelineStage,
        readiness,
        readinessReady,
        locationState,
      });
    }
    return list;
  }, [adminUserById, billingDefaultsByPartnerId, derivedOkByPartnerId, flows, locationById, now, partnerById, partnerSeasonById, squadById]);

  const vmByFlowId = useMemo(() => {
    const m = new Map<string, FlowVM>();
    for (const vm of vms) m.set(String(vm.flowId || ''), vm);
    return m;
  }, [vms]);

  const filtersActive = useMemo(() => {
    return (
      safeString(queryText) !== ''
      || sortBy !== 'priority'
      || dueFilter !== 'all'
      || riskMin !== 0
      || riskMax !== 10
      || cpPctMin !== 0
      || cpPctMax !== 100
      || overallPctMin !== 0
      || overallPctMax !== 100
      || assigneeFilter !== 'all'
    );
  }, [assigneeFilter, cpPctMax, cpPctMin, dueFilter, overallPctMax, overallPctMin, queryText, riskMax, riskMin, sortBy]);

  const filtered: FlowVM[] = useMemo(() => {
    const needle = safeString(queryText).toLowerCase();
    const rMin = clamp(Number(riskMin), 0, 10);
    const rMax = clamp(Number(riskMax), 0, 10);
    const cpMin = clamp(Number(cpPctMin), 0, 100);
    const cpMax = clamp(Number(cpPctMax), 0, 100);
    const oMin = clamp(Number(overallPctMin), 0, 100);
    const oMax = clamp(Number(overallPctMax), 0, 100);

    const passDue = (vm: FlowVM): boolean => {
      const d = vm.todoDaysToDue;
      if (dueFilter === 'all') return true;
      if (dueFilter === 'no_due') return d === null;
      if (d === null) return false;
      if (dueFilter === 'overdue') return d < 0;
      if (dueFilter === 'due_3') return d >= 0 && d <= 3;
      if (dueFilter === 'due_7') return d >= 0 && d <= 7;
      if (dueFilter === 'due_14') return d >= 0 && d <= 14;
      return true;
    };

    return vms.filter((vm) => {
      if (needle) {
        const hit =
          vm.squadTitle.toLowerCase().includes(needle)
          || vm.partnerName.toLowerCase().includes(needle)
          || vm.locationNames.some((name) => name.toLowerCase().includes(needle))
          || vm.squadId.toLowerCase().includes(needle);
        if (!hit) return false;
      }
      if (assigneeFilter === 'unassigned') {
        if (safeString(vm.ownerUserId)) return false;
      } else if (assigneeFilter !== 'all') {
        if (safeString(vm.ownerUserId) !== assigneeFilter) return false;
      }
      const risk = vm.priority.risk;
      if (risk < Math.min(rMin, rMax) || risk > Math.max(rMin, rMax)) return false;
      if (vm.cpPercent < Math.min(cpMin, cpMax) || vm.cpPercent > Math.max(cpMin, cpMax)) return false;
      const overall = vm.priority.overallPercentComplete;
      if (overall < Math.min(oMin, oMax) || overall > Math.max(oMin, oMax)) return false;
      if (!passDue(vm)) return false;
      return true;
    });
  }, [assigneeFilter, cpPctMax, cpPctMin, dueFilter, overallPctMax, overallPctMin, queryText, riskMax, riskMin, vms]);

  const saveAssignee = async (flowId: string, userIdOrNull: string | null) => {
    const ref = doc(db, 'partnerSeasonFlows', flowId);
    const nowMs = Date.now();
    const actor = String((userData as any)?.id || '');
    await updateDoc(ref, {
      internalOwnerUserId: userIdOrNull,
      updatedAt: nowMs,
      ...(actor ? { updatedBy: actor } : {}),
    } as any);
  };

  const moveFlowToStage = useCallback(async (flowId: string, toStage: OpsPipelineStageId, fromStage?: OpsPipelineStageId) => {
    const id = String(flowId || '').trim();
    if (!id) return;
    if (fromStage && fromStage === toStage) return;
    const actor = String((userData as any)?.id || '');
    const nowMs = Date.now();

    // Optimistic local update so the card moves immediately.
    setFlows((prev) => prev.map((f) => (
      String((f as any)?.id || '') === id
        ? ({ ...(f as any), pipelineStage: toStage, pipelineStageUpdatedAt: nowMs, pipelineStageUpdatedBy: actor, updatedAt: nowMs } as OpsFlow)
        : f
    )));

    setSavingStageFlowId(id);
    try {
      await updateDoc(doc(db, 'partnerSeasonFlows', id), {
        pipelineStage: toStage,
        pipelineStageUpdatedAt: nowMs,
        pipelineStageUpdatedBy: actor,
        updatedAt: nowMs,
        ...(actor ? { updatedBy: actor } : {}),
      } as any);
      await addDoc(collection(db, 'partnerSeasonFlows', id, 'stageHistory'), {
        ...(fromStage ? { from: fromStage } : {}),
        to: toStage,
        movedAt: nowMs,
        movedBy: actor,
      } as any);
    } finally {
      setSavingStageFlowId((cur) => (cur === id ? null : cur));
    }
  }, [userData]);

  const saveFlowTodoNote = useCallback(async (flowId: string, noteRaw: string) => {
    const actor = String((userData as any)?.id || '');
    const note = String(noteRaw ?? '').trim();
    const ref = doc(db, 'partnerSeasonFlows', flowId);
    await updateDoc(ref, {
      'flowTodo.note': note ? note : null,
      updatedAt: Date.now(),
      ...(actor ? { updatedBy: actor } : {}),
    } as any);
  }, [userData]);

  const saveFlowTodoDueDate = useCallback(async (flowId: string, ymdRaw: string) => {
    const actor = String((userData as any)?.id || '');
    const ymd = String(ymdRaw ?? '').trim();
    const ref = doc(db, 'partnerSeasonFlows', flowId);
    await updateDoc(ref, {
      'flowTodo.dueDate': ymd ? ymd : null,
      updatedAt: Date.now(),
      ...(actor ? { updatedBy: actor } : {}),
    } as any);
  }, [userData]);

  useEffect(() => {
    if (!todoEditor) return;
    const t = window.setTimeout(() => {
      todoTextareaRef.current?.focus();
      todoTextareaRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [todoEditor?.flowId]);

  useEffect(() => {
    if (!dueEditor) return;
    const t = window.setTimeout(() => {
      dueInputRef.current?.focus();
      // Some browsers support showing the picker programmatically.
      try { (dueInputRef.current as any)?.showPicker?.(); } catch { /* ignore */ }
    }, 0);
    return () => window.clearTimeout(t);
  }, [dueEditor?.flowId]);

  const sorted: FlowVM[] = useMemo(() => {
    const out = filtered.slice();
    out.sort((a, b) => {
      if (sortBy === 'priority') return compareOpsFlowPriority(a.priority, b.priority);
      if (sortBy === 'due') {
        const ad = a.todoDaysToDue === null ? Number.POSITIVE_INFINITY : a.todoDaysToDue;
        const bd = b.todoDaysToDue === null ? Number.POSITIVE_INFINITY : b.todoDaysToDue;
        if (ad !== bd) return ad - bd;
        return compareOpsFlowPriority(a.priority, b.priority);
      }
      if (sortBy === 'risk') {
        if (b.priority.risk !== a.priority.risk) return b.priority.risk - a.priority.risk;
        return compareOpsFlowPriority(a.priority, b.priority);
      }
      if (sortBy === 'updated') {
        if (b.updatedAt !== a.updatedAt) return b.updatedAt - a.updatedAt;
        return compareOpsFlowPriority(a.priority, b.priority);
      }
      // name
      const an = `${a.partnerName} ${a.locationNames.join(' ')} ${a.squadTitle}`.toLowerCase();
      const bn = `${b.partnerName} ${b.locationNames.join(' ')} ${b.squadTitle}`.toLowerCase();
      return an.localeCompare(bn);
    });
    return out;
  }, [filtered, sortBy]);

  const top10: FlowVM[] = useMemo(() => {
    const base = sorted.slice().sort((a, b) => compareOpsFlowPriority(a.priority, b.priority));
    const TOP_N = 10;
    let top = base.slice(0, TOP_N);

    // Optional data hygiene: reserve 1 slot for highest-risk "no due date" flow (if any).
    const noDue = base.filter((x) => x.needsDueDate);
    if (noDue.length > 0) {
      noDue.sort((a, b) => {
        if (b.priority.risk !== a.priority.risk) return b.priority.risk - a.priority.risk;
        if (b.priority.priorityScore !== a.priority.priorityScore) return b.priority.priorityScore - a.priority.priorityScore;
        return `${a.partnerName}/${a.squadTitle}`.localeCompare(`${b.partnerName}/${b.squadTitle}`);
      });
      const pick = noDue[0];
      const already = top.some((t) => t.flowId === pick.flowId);
      if (!already) {
        if (top.length < TOP_N) top = [...top, pick];
        else if (top.length > 0) top = [...top.slice(0, TOP_N - 1), pick];
      }
    }

    return top;
  }, [sorted]);

  const boardFiltersActive = useMemo(() => (
    safeString(boardSearch) !== ''
    || boardAssignee !== 'all'
    || boardDue !== 'all'
    || boardPriority !== 'all'
    || boardFlags !== 'all'
    || boardLocation !== 'all'
  ), [boardAssignee, boardDue, boardFlags, boardLocation, boardPriority, boardSearch]);

  const boardStateOptions = useMemo(() => {
    const codes = new Set<string>();
    let hasUnknown = false;
    for (const vm of vms) {
      if (vm.locationState) codes.add(vm.locationState);
      else hasUnknown = true;
    }
    const sorted = Array.from(codes).sort((a, b) => (US_STATES[a] || a).localeCompare(US_STATES[b] || b));
    return { codes: sorted, hasUnknown };
  }, [vms]);

  const boardFiltered: FlowVM[] = useMemo(() => {
    const needle = safeString(boardSearch).toLowerCase();

    const passDue = (vm: FlowVM): boolean => {
      const d = vm.todoDaysToDue;
      if (boardDue === 'all') return true;
      if (boardDue === 'no_due') return d === null;
      if (d === null) return false;
      if (boardDue === 'overdue') return d < 0;
      if (boardDue === 'due_3') return d >= 0 && d <= 3;
      if (boardDue === 'due_7') return d >= 0 && d <= 7;
      if (boardDue === 'due_14') return d >= 0 && d <= 14;
      return true;
    };

    const passPriority = (vm: FlowVM): boolean => {
      if (boardPriority === 'all') return true;
      const s = Number(vm.priority.priorityScore) || 0;
      if (boardPriority === 'high') return s >= 67;
      if (boardPriority === 'medium') return s >= 34 && s < 67;
      return s < 34; // low
    };

    const passFlags = (vm: FlowVM): boolean => {
      if (boardFlags === 'all') return true;
      if (boardFlags === 'no_due') return vm.needsDueDate;
      // attention: missing logistics or no due date
      return vm.needsDueDate || (vm.readinessReady && vm.readiness.missingCount > 0);
    };

    return vms.filter((vm) => {
      if (needle) {
        const hit =
          vm.squadTitle.toLowerCase().includes(needle)
          || vm.partnerName.toLowerCase().includes(needle)
          || vm.locationNames.some((name) => name.toLowerCase().includes(needle))
          || vm.squadId.toLowerCase().includes(needle);
        if (!hit) return false;
      }
      if (boardAssignee === 'unassigned') {
        if (safeString(vm.ownerUserId)) return false;
      } else if (boardAssignee !== 'all') {
        if (safeString(vm.ownerUserId) !== boardAssignee) return false;
      }
      if (boardLocation !== 'all') {
        if (boardLocation === 'unknown') {
          if (vm.locationState) return false;
        } else if (vm.locationState !== boardLocation) {
          return false;
        }
      }
      if (!passPriority(vm)) return false;
      if (!passDue(vm)) return false;
      if (!passFlags(vm)) return false;
      return true;
    });
  }, [vms, boardSearch, boardAssignee, boardDue, boardPriority, boardFlags, boardLocation]);

  const boardGroups = useMemo(() => {
    const byId = {} as Record<OpsPipelineStageId, FlowVM[]>;
    for (const stage of OPS_PIPELINE_STAGES) byId[stage.id] = [];

    for (const vm of boardFiltered) {
      const stage = vm.pipelineStage in byId ? vm.pipelineStage : 'uncategorized';
      byId[stage].push(vm);
    }

    // Keep columns internally sorted by priority so the most urgent surface first.
    (Object.keys(byId) as OpsPipelineStageId[]).forEach((id) => {
      byId[id].sort((a, b) => compareOpsFlowPriority(a.priority, b.priority));
    });

    return byId;
  }, [boardFiltered]);

  const monthlyStudentTotals = useMemo(() => {
    // Show 3 months: current, next, next+1 (UTC-based, deterministic).
    const currentMonthStartUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
    const monthStarts = [
      currentMonthStartUtc,
      addMonthsUtc(currentMonthStartUtc, 1),
      addMonthsUtc(currentMonthStartUtc, 2),
    ];

    return monthStarts.map((ms) => {
      const next = addMonthsUtc(ms, 1);
      let total = 0;
      for (const vm of vms) {
        const r = vm.scheduleRangeUtc;
        if (!r) continue;
        if (!rangesOverlap(r.start, r.endExclusive, ms, next)) continue;
        total += Math.max(0, Number(vm.studentCount) || 0);
      }
      return { monthStartUtc: ms, label: monthLabelUtc(ms), total };
    });
  }, [now, vms]);

  const rosterSummary = useMemo(() => {
    const enrolledSquads = vms.filter((vm) => (Number(vm.studentCount) || 0) > 0);
    const enrolledSquadsCount = enrolledSquads.length;
    const enrolledStudentsTotal = enrolledSquads.reduce((acc, vm) => acc + (Number(vm.studentCount) || 0), 0);
    const avgKidsPerSquad = enrolledSquadsCount > 0 ? enrolledStudentsTotal / enrolledSquadsCount : 0;
    return { enrolledSquadsCount, avgKidsPerSquad };
  }, [vms]);

  const openFlow = (flowId: string) => {
    navigate(`/app/admin/ops/flows/${flowId}`);
  };

  const toggleCardExpanded = useCallback((flowId: string) => {
    const id = String(flowId || '').trim();
    if (!id) return;
    setExpandedCardIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const openSquadStudentsModal = useCallback((squadId: string) => {
    const normalizedSquadId = String(squadId || '').trim();
    if (!normalizedSquadId) return;
    setManageStudentsSquadId(normalizedSquadId);
  }, []);

  const closeSquadStudentsModal = useCallback(() => {
    setManageStudentsSquadId(null);
  }, []);

  const manageStudentsSquad = useMemo(() => {
    if (!manageStudentsSquadId) return null;
    return squadById.get(manageStudentsSquadId) || null;
  }, [manageStudentsSquadId, squadById]);

  const handleManageStudentsSquadUpdate = useCallback((updatedSquad: Squad) => {
    const updatedId = String((updatedSquad as any)?.id || '').trim();
    if (!updatedId) return;
    setSquads((prev) => prev.map((s) => (String((s as any)?.id || '').trim() === updatedId ? updatedSquad : s)));
  }, []);

  const handleRowClick = (flowId: string) => (e: React.MouseEvent<HTMLTableRowElement>) => {
    const target = e.target as HTMLElement | null;
    if (target) {
      // Don't steal clicks from existing interactive controls inside the row.
      const interactive = target.closest('button,a,input,select,textarea,[role="button"],[role="link"]');
      if (interactive) return;
    }
    openFlow(flowId);
  };

  return (
    <div className="ops-flows-page">
      <div className="ops-flows-header">
        <div>
          <h2 className="ops-flows-title">Flows</h2>
          <div className="ops-flows-subtitle">
            Triage what matters most, then jump straight into the flow.
          </div>
          <div className="ops-flows-view-tabs" role="tablist" aria-label="Flows view">
            <button
              type="button"
              className={`ops-flows-view-tab ${view === 'browse' ? 'is-active' : ''}`}
              onClick={() => setView('browse')}
              role="tab"
              aria-selected={view === 'browse'}
            >
              Browse
            </button>
            <button
              type="button"
              className={`ops-flows-view-tab ${view === 'overview' ? 'is-active' : ''}`}
              onClick={() => setView('overview')}
              role="tab"
              aria-selected={view === 'overview'}
            >
              Overview
            </button>
          </div>
        </div>
        <div className="ops-flows-header-right">
          <span className="ops-pill">{filtered.length} shown</span>
          <span className="ops-pill">{vms.length} total</span>
          <button
            type="button"
            className="ops-button ops-button-primary"
            onClick={() => navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'squads', openCreateSquadForm: true } })}
            title="Create a new squad"
          >
            + Create squad
          </button>
          <button
            type="button"
            className="ops-button"
            onClick={() => {
              setQueryText('');
              setSortBy('priority');
              setDueFilter('all');
              setRiskMin(0);
              setRiskMax(10);
              setCpPctMin(0);
              setCpPctMax(100);
              setOverallPctMin(0);
              setOverallPctMax(100);
            }}
            title="Reset all filters"
            disabled={!filtersActive}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="ops-card" style={{ marginBottom: 12 }}>
        <div className="ops-card-title-row">
          <div className="ops-card-title">Students registered</div>
        </div>
        <div className="ops-monthly-students-wrap">
          <div className="ops-monthly-students-grid">
            {monthlyStudentTotals.map((m) => (
              <div key={m.monthStartUtc.getTime()} className="ops-monthly-students-item" title={m.label}>
                <div className="ops-monthly-students-month">{m.label}</div>
                <div className="ops-monthly-students-total ops-mono">{m.total}</div>
              </div>
            ))}
          </div>
          <div className="ops-monthly-students-side" aria-label="Roster summary">
            <div className="ops-monthly-students-metric">
              <div className="ops-monthly-students-metric-label">Avg students</div>
              <div className="ops-monthly-students-metric-value ops-mono">
                {rosterSummary.avgKidsPerSquad >= 10
                  ? Math.round(rosterSummary.avgKidsPerSquad)
                  : rosterSummary.avgKidsPerSquad.toFixed(1)}
              </div>
            </div>
            <div className="ops-monthly-students-metric">
              <div className="ops-monthly-students-metric-label">Squads w/students</div>
              <div className="ops-monthly-students-metric-value ops-mono">
                {rosterSummary.enrolledSquadsCount}
              </div>
            </div>
          </div>
        </div>
      </div>

      {view === 'browse' ? (
        <>
          <div className="ops-card">
            <div className="ops-card-title-row">
              <div className="ops-card-title">Top 10 priorities</div>
              <div className="ops-card-actions">
                <span className="ops-pill">Score = 0–100</span>
              </div>
            </div>
            <div className="ops-priority-grid">
              {top10.length === 0 ? (
                <div className="muted" style={{ padding: 6 }}>No flows match your filters.</div>
              ) : (
                top10.map((vm) => {
                  const tone = dueBadgeTone(vm.todoDaysToDue);
                  const badgeClass =
                    tone === 'danger' ? 'ops-badge ops-badge-danger'
                    : tone === 'warn' ? 'ops-badge ops-badge-warn'
                    : 'ops-badge ops-badge-muted';
                  return (
                    <button
                      key={vm.flowId}
                      type="button"
                      className="ops-priority-card"
                      onClick={() => navigate(`/app/admin/ops/flows/${vm.flowId}`)}
                      title="Open flow"
                    >
                      <div className="ops-priority-topline">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span className="ops-priority-score ops-mono" aria-label={`Score ${Math.round(vm.priority.priorityScore)}`}>
                            {Math.round(vm.priority.priorityScore)}
                          </span>
                        </div>
                        <span className={badgeClass}>
                          {vm.needsDueDate ? 'No due date' : vm.todoDueLabel}
                        </span>
                      </div>

                      <div className="ops-priority-name">{vm.squadTitle}</div>

                      <div className="ops-kv" style={{ marginTop: 10 }}>
                        <div className="ops-kv-row ops-kv-row-todo">
                          <span className="ops-kv-key">Todo</span>
                          <span className="ops-kv-value ops-kv-value-todo" title={vm.todo || undefined}>{safeString(vm.todo) ? vm.todo : '—'}</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="ops-card">
            <div className="ops-card-title-row">
              <div className="ops-card-title">Browse & filter</div>
            </div>

            <div className="ops-filters">
              <div className="ops-filters-grid">
                <div className="ops-field">
                  <div className="ops-label">Search</div>
                  <input
                    className="ops-input"
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    placeholder="Partner, location, or squad…"
                  />
                </div>

                <div className="ops-field">
                  <div className="ops-label">Sort</div>
                  <select className="ops-select" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)}>
                    <option value="priority">Priority (recommended)</option>
                    <option value="due">Due date (soonest)</option>
                    <option value="risk">Priority (manual 0–10)</option>
                    <option value="updated">Recently updated</option>
                    <option value="name">Name (A→Z)</option>
                  </select>
                </div>

                <div className="ops-field">
                  <div className="ops-label">Due date</div>
                  <select className="ops-select" value={dueFilter} onChange={(e) => setDueFilter(e.target.value as DueFilter)}>
                    <option value="all">All</option>
                    <option value="overdue">Overdue</option>
                    <option value="due_3">Due in 3 days</option>
                    <option value="due_7">Due in 7 days</option>
                    <option value="due_14">Due in 14 days</option>
                    <option value="no_due">No due date</option>
                  </select>
                </div>

                <div className="ops-field">
                  <div className="ops-label">Assigned</div>
                  <select className="ops-select" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="unassigned">Unassigned</option>
                    {adminUsers.map((u) => {
                      const id = String((u as any)?.id || '').trim();
                      if (!id) return null;
                      const label = String((u as any)?.displayName || (u as any)?.email || id).trim() || id;
                      return <option key={id} value={id}>{label}</option>;
                    })}
                  </select>
                </div>

                <div className="ops-field">
                  <div className="ops-label">Priority (0–10)</div>
                  <div className="ops-two-col">
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(riskMin)}
                      onChange={(e) => setRiskMin(clamp(Number(e.target.value), 0, 10))}
                      aria-label="Minimum priority"
                    />
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(riskMax)}
                      onChange={(e) => setRiskMax(clamp(Number(e.target.value), 0, 10))}
                      aria-label="Maximum priority"
                    />
                  </div>
                </div>

                <div className="ops-field">
                  <div className="ops-label">Checkpoint % (0–100)</div>
                  <div className="ops-two-col">
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(cpPctMin)}
                      onChange={(e) => setCpPctMin(clamp(Number(e.target.value), 0, 100))}
                      aria-label="Minimum checkpoint percent"
                    />
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(cpPctMax)}
                      onChange={(e) => setCpPctMax(clamp(Number(e.target.value), 0, 100))}
                      aria-label="Maximum checkpoint percent"
                    />
                  </div>
                </div>

                <div className="ops-field">
                  <div className="ops-label">Overall % (0–100)</div>
                  <div className="ops-two-col">
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(overallPctMin)}
                      onChange={(e) => setOverallPctMin(clamp(Number(e.target.value), 0, 100))}
                      aria-label="Minimum overall percent"
                    />
                    <input
                      className="ops-input"
                      inputMode="numeric"
                      value={String(overallPctMax)}
                      onChange={(e) => setOverallPctMax(clamp(Number(e.target.value), 0, 100))}
                      aria-label="Maximum overall percent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="ops-table-wrap">
              {sorted.length === 0 ? (
                <div className="muted" style={{ padding: 14 }}>No flows match your filters.</div>
              ) : (
                <>
                  <table className="ops-table">
                    <thead>
                      <tr>
                        <th>Squad</th>
                        <th>Todo (next step)</th>
                        <th title="Assigned">👤</th>
                        <th className="ops-right">Score</th>
                        <th>Stage</th>
                        <th className="ops-right" title="Students currently signed up">Students</th>
                        <th>Due</th>
                        <th className="ops-right">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((vm) => {
                        const tone = dueBadgeTone(vm.todoDaysToDue);
                        const badgeClass =
                          tone === 'danger' ? 'ops-badge ops-badge-danger'
                          : tone === 'warn' ? 'ops-badge ops-badge-warn'
                          : 'ops-badge ops-badge-muted';
                        return (
                          <tr key={vm.flowId} className="ops-clickable-row" onClick={handleRowClick(vm.flowId)}>
                            <td>
                              <span
                                className="ops-link"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/app/admin/ops/flows/${vm.flowId}`)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') navigate(`/app/admin/ops/flows/${vm.flowId}`);
                                }}
                              >
                                {vm.squadTitle}
                              </span>
                            </td>
                            <td title={vm.todo || undefined}>
                              {todoEditor?.flowId === vm.flowId ? (
                                <textarea
                                  ref={todoTextareaRef}
                                  value={todoEditor.draft}
                                  rows={2}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setTodoEditor((prev) => (prev ? { ...prev, draft: e.target.value } : prev))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.currentTarget.blur();
                                      return;
                                    }
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  onBlur={() => {
                                    const flowId = vm.flowId;
                                    const draft = String(todoEditor.draft ?? '');
                                    const initial = String(todoEditor.initial ?? '');
                                    setTodoEditor(null);
                                    setSavingTodoFlowId(flowId);
                                    void (async () => {
                                      try {
                                        if (draft.trim() === initial.trim()) return;
                                        await saveFlowTodoNote(flowId, draft);
                                      } finally {
                                        setSavingTodoFlowId((cur) => (cur === flowId ? null : cur));
                                      }
                                    })();
                                  }}
                                  style={{
                                    width: '100%',
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    color: '#111827',
                                    fontWeight: 600,
                                    resize: 'vertical',
                                    padding: '6px 8px',
                                    borderRadius: 10,
                                    border: '1px solid #e5e7eb',
                                    background: 'white',
                                    minHeight: 44,
                                  }}
                                  placeholder="Add a TODO…"
                                  aria-label="Edit todo"
                                />
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDueEditor(null);
                                    const current = safeString(vm.todo) ? String(vm.todo) : '';
                                    setTodoEditor({ flowId: vm.flowId, draft: current, initial: current });
                                  }}
                                  className="ops-link"
                                  style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    background: 'transparent',
                                    border: 0,
                                    padding: 0,
                                    fontSize: 12,
                                    lineHeight: 1.35,
                                    color: safeString(vm.todo) ? '#374151' : '#9ca3af',
                                    whiteSpace: 'normal',
                                    cursor: 'text',
                                    opacity: savingTodoFlowId === vm.flowId ? 0.6 : 1,
                                  }}
                                  title="Click to edit todo"
                                >
                                  {safeString(vm.todo) ? vm.todo : '—'}
                                </button>
                              )}
                            </td>
                            <td
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: 56 }}
                            >
                              <button
                                type="button"
                                className="mini-btn"
                                onClick={(evt) => {
                                  evt.stopPropagation();
                                  const rect = (evt.currentTarget as HTMLElement).getBoundingClientRect();
                                  setAssigneePicker({ flowId: vm.flowId, anchorRect: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom } });
                                }}
                                title="Assign owner"
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 999,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                {vm.ownerUser ? (
                                  <UserAvatar
                                    src={String((vm.ownerUser as any)?.headshotUrl || '') || undefined}
                                    username={String((vm.ownerUser as any)?.displayName || (vm.ownerUser as any)?.email || '') || undefined}
                                    size="small"
                                    variant="circular"
                                  />
                                ) : (
                                  '—'
                                )}
                              </button>
                            </td>
                            <td className="ops-right ops-mono">{Math.round(vm.priority.priorityScore)}</td>
                            <td>
                              <span className={`ops-stage-chip ops-stage-chip--${vm.pipelineStage}`} title={`Stage: ${pipelineStageLabel(vm.pipelineStage)}`}>
                                {pipelineStageLabel(vm.pipelineStage)}
                              </span>
                            </td>
                            <td className="ops-right ops-mono" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="ops-link"
                                onClick={() => openSquadStudentsModal(vm.squadId)}
                                style={{
                                  background: 'transparent',
                                  border: 0,
                                  padding: 0,
                                  font: 'inherit',
                                  color: 'inherit',
                                  cursor: vm.squadId ? 'pointer' : 'default',
                                }}
                                disabled={!vm.squadId}
                                title="Manage students"
                              >
                                {vm.studentCount}
                              </button>
                            </td>
                            <td>
                              {dueEditor?.flowId === vm.flowId ? (
                                <input
                                  ref={dueInputRef}
                                  type="date"
                                  value={dueEditor.draftYmd}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    const next = String(e.target.value || '');
                                    setDueEditor((prev) => (prev ? { ...prev, draftYmd: next } : prev));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      (e.currentTarget as HTMLInputElement).blur();
                                    }
                                    if (e.key === 'Enter') {
                                      (e.currentTarget as HTMLInputElement).blur();
                                    }
                                  }}
                                  onBlur={() => {
                                    const flowId = vm.flowId;
                                    const draftYmd = String(dueEditor.draftYmd || '');
                                    const initialYmd = String(dueEditor.initialYmd || '');
                                    setDueEditor(null);
                                    setSavingDueFlowId(flowId);
                                    void (async () => {
                                      try {
                                        if (draftYmd === initialYmd) return;
                                        await saveFlowTodoDueDate(flowId, draftYmd);
                                      } finally {
                                        setSavingDueFlowId((cur) => (cur === flowId ? null : cur));
                                      }
                                    })();
                                  }}
                                  style={{
                                    padding: '6px 8px',
                                    borderRadius: 10,
                                    border: '1px solid #e5e7eb',
                                    fontSize: 12,
                                  }}
                                  aria-label="Edit due date"
                                />
                              ) : (
                                <button
                                  type="button"
                                  className={badgeClass}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTodoEditor(null);
                                    const flowAny: any = vm.flow as any;
                                    const ft: any = Object.prototype.hasOwnProperty.call(flowAny || {}, 'flowTodo') ? (flowAny.flowTodo || {}) : null;
                                    const curYmd = ft ? String(ft?.dueDate || '').slice(0, 10) : '';
                                    const initialYmd =
                                      curYmd
                                      || (vm.todoDaysToDue !== null && !vm.needsDueDate ? ymdFromDaysToDueUtc(vm.todoDaysToDue) : '');
                                    setDueEditor({ flowId: vm.flowId, draftYmd: initialYmd, initialYmd });
                                  }}
                                  title="Click to edit due date"
                                  style={{
                                    cursor: 'pointer',
                                    opacity: savingDueFlowId === vm.flowId ? 0.6 : 1,
                                  }}
                                >
                                  {vm.needsDueDate ? 'No due date' : vm.todoDueLabel}
                                </button>
                              )}
                            </td>
                            <td className="ops-right ops-mono">{vm.updatedAt ? daysAgoShort(now, vm.updatedAt) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="ops-mobile-list" aria-label="Flows list">
                    {sorted.map((vm) => {
                      const tone = dueBadgeTone(vm.todoDaysToDue);
                      const badgeClass =
                        tone === 'danger' ? 'ops-badge ops-badge-danger'
                        : tone === 'warn' ? 'ops-badge ops-badge-warn'
                        : 'ops-badge ops-badge-muted';
                      return (
                        <button
                          key={vm.flowId}
                          type="button"
                          className="ops-mobile-card"
                          onClick={() => navigate(`/app/admin/ops/flows/${vm.flowId}`)}
                          title="Open flow"
                        >
                          <div className="ops-mobile-top">
                            <div className="ops-mobile-title">{vm.squadTitle}</div>
                            <div className="ops-mobile-score ops-mono">{Math.round(vm.priority.priorityScore)}</div>
                          </div>
                          <div className="ops-mobile-sub" title={vm.todo || undefined}>
                            TODO: {vm.todoShort}
                          </div>
                          <div className="ops-mobile-row">
                            <span className={badgeClass}>{vm.needsDueDate ? 'No due date' : vm.todoDueLabel}</span>
                            <span className="ops-mobile-chip ops-mono">Students {vm.studentCount}</span>
                          </div>
                          <div className="ops-mobile-meta ops-mono">
                            {vm.partnerName}{vm.locationNames.length ? ` • ${vm.locationNames.join(', ')}` : ''} •{" "}
                            Updated {vm.updatedAt ? daysAgoShort(now, vm.updatedAt) : '—'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="ops-card ops-board-card-wrap">
          <div className="ops-card-title-row">
            <div className="ops-card-title">Overview</div>
            <div className="ops-card-actions">
              <span className="ops-pill">{boardFiltered.length} shown</span>
              <span className="ops-pill">Drag a card or use the stage picker</span>
              <button
                type="button"
                className="ops-button"
                onClick={() => {
                  setBoardSearch('');
                  setBoardAssignee('all');
                  setBoardDue('all');
                  setBoardPriority('all');
                  setBoardFlags('all');
                  setBoardLocation('all');
                }}
                disabled={!boardFiltersActive}
                title="Reset board filters"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="ops-filters ops-board-filters">
            <div className="ops-filters-grid">
              <div className="ops-field">
                <div className="ops-label">Search</div>
                <input
                  className="ops-input"
                  value={boardSearch}
                  onChange={(e) => setBoardSearch(e.target.value)}
                  placeholder="Partner, location, or squad…"
                />
              </div>

              <div className="ops-field">
                <div className="ops-label">Assigned</div>
                <select className="ops-select" value={boardAssignee} onChange={(e) => setBoardAssignee(e.target.value)}>
                  <option value="all">All</option>
                  <option value="unassigned">Unassigned</option>
                  {adminUsers.map((u) => {
                    const id = String((u as any)?.id || '').trim();
                    if (!id) return null;
                    const label = String((u as any)?.displayName || (u as any)?.email || id).trim() || id;
                    return <option key={id} value={id}>{label}</option>;
                  })}
                </select>
              </div>

              <div className="ops-field">
                <div className="ops-label">Location (state)</div>
                <select className="ops-select" value={boardLocation} onChange={(e) => setBoardLocation(e.target.value)}>
                  <option value="all">All</option>
                  {boardStateOptions.codes.map((code) => (
                    <option key={code} value={code}>{US_STATES[code] || code}</option>
                  ))}
                  {boardStateOptions.hasUnknown ? <option value="unknown">Unknown</option> : null}
                </select>
              </div>

              <div className="ops-field">
                <div className="ops-label">Priority</div>
                <select className="ops-select" value={boardPriority} onChange={(e) => setBoardPriority(e.target.value as typeof boardPriority)}>
                  <option value="all">All</option>
                  <option value="high">High (67–100)</option>
                  <option value="medium">Medium (34–66)</option>
                  <option value="low">Low (0–33)</option>
                </select>
              </div>

              <div className="ops-field">
                <div className="ops-label">Due date</div>
                <select className="ops-select" value={boardDue} onChange={(e) => setBoardDue(e.target.value as DueFilter)}>
                  <option value="all">All</option>
                  <option value="overdue">Overdue</option>
                  <option value="due_3">Due in 3 days</option>
                  <option value="due_7">Due in 7 days</option>
                  <option value="due_14">Due in 14 days</option>
                  <option value="no_due">No due date</option>
                </select>
              </div>

              <div className="ops-field">
                <div className="ops-label">Flags</div>
                <select className="ops-select" value={boardFlags} onChange={(e) => setBoardFlags(e.target.value as typeof boardFlags)}>
                  <option value="all">All</option>
                  <option value="attention">Needs attention</option>
                  <option value="no_due">No due date</option>
                </select>
              </div>
            </div>
          </div>

          <div className="ops-board" role="list">
            {OPS_PIPELINE_STAGES
              .filter((stage) => stage.id !== 'uncategorized' || (boardGroups[stage.id] || []).length > 0)
              .map((stage) => {
                const cards = boardGroups[stage.id] || [];
                const isOver = dragOverStage === stage.id;
                return (
                  <section
                    key={stage.id}
                    className={`ops-column ops-column--${stage.id}${isOver ? ' is-drop-target' : ''}`}
                    onDragOver={(e) => {
                      if (!draggingFlowId) return;
                      e.preventDefault();
                      try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
                      if (dragOverStage !== stage.id) setDragOverStage(stage.id);
                    }}
                    onDragLeave={(e) => {
                      if (e.currentTarget === e.target) setDragOverStage((cur) => (cur === stage.id ? null : cur));
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const flowId = draggingFlowId || e.dataTransfer.getData('text/plain');
                      setDragOverStage(null);
                      setDraggingFlowId(null);
                      if (!flowId) return;
                      const dragged = vmByFlowId.get(flowId) || null;
                      void moveFlowToStage(flowId, stage.id, dragged?.pipelineStage);
                    }}
                  >
                    <header className="ops-column-header" title={stage.description}>
                      <span className="ops-column-title">{stage.label}</span>
                      <span className="ops-column-count">{cards.length}</span>
                    </header>

                    <div className="ops-column-body">
                      {cards.length === 0 ? (
                        <div className="ops-column-empty">No squads</div>
                      ) : (
                        cards.map((vm) => {
                          const tone = dueBadgeTone(vm.todoDaysToDue);
                          const dueBadgeClass =
                            tone === 'danger' ? 'ops-badge ops-badge-danger'
                            : tone === 'warn' ? 'ops-badge ops-badge-warn'
                            : 'ops-badge ops-badge-muted';
                          const isEditingTodo = todoEditor?.flowId === vm.flowId;
                          const isEditingDue = dueEditor?.flowId === vm.flowId;
                          const badgesToShow = vm.readinessReady
                            ? vm.readiness.badges
                            : vm.readiness.badges.filter((b) => b.id === 'no_due');
                          const isExpanded = Boolean(expandedCardIds[vm.flowId]);
                          return (
                            <article
                              key={vm.flowId}
                              className={`ops-board-card${isExpanded ? ' is-expanded' : ' is-collapsed'}${draggingFlowId === vm.flowId ? ' is-dragging' : ''}${savingStageFlowId === vm.flowId ? ' is-saving' : ''}`}
                              draggable={!isEditingTodo && !isEditingDue}
                              onDragStart={(e) => {
                                setDraggingFlowId(vm.flowId);
                                try {
                                  e.dataTransfer.setData('text/plain', vm.flowId);
                                  e.dataTransfer.effectAllowed = 'move';
                                } catch { /* ignore */ }
                              }}
                              onDragEnd={() => { setDraggingFlowId(null); setDragOverStage(null); }}
                            >
                              <div className="ops-board-card-top">
                                <button
                                  type="button"
                                  className="ops-board-card-toggle"
                                  onClick={() => toggleCardExpanded(vm.flowId)}
                                  aria-expanded={isExpanded}
                                  title={isExpanded ? 'Hide details' : 'Show details'}
                                >
                                  <span className="ops-board-card-caret" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                                </button>
                                <button
                                  type="button"
                                  className="ops-board-card-title ops-link"
                                  onClick={() => navigate(`/app/admin/ops/flows/${vm.flowId}`)}
                                  title="Open flow"
                                >
                                  {vm.squadTitle}
                                </button>
                                <div className="ops-board-card-nums">
                                  {!isExpanded && badgesToShow.length > 0 ? (
                                    <span className="ops-board-card-flag" title={badgesToShow.map((b) => b.label).join(', ')} aria-label={`${badgesToShow.length} readiness flags`}>
                                      {badgesToShow.length}
                                    </span>
                                  ) : (
                                    <span className="ops-board-card-flag is-empty" aria-hidden="true" />
                                  )}
                                  <span className="ops-priority-score ops-mono" title={`Priority ${Math.round(vm.priority.priorityScore)}`}>
                                    {Math.round(vm.priority.priorityScore)}
                                  </span>
                                </div>
                              </div>

                              {isExpanded ? (
                              <>
                              <div className="ops-board-card-school" title={vm.partnerName}>
                                {vm.partnerName}{vm.locationNames.length ? ` • ${vm.locationNames.join(', ')}` : ''}
                              </div>

                              <div className="ops-board-card-meta">
                                {isEditingDue ? (
                                  <input
                                    ref={dueInputRef}
                                    type="date"
                                    value={dueEditor.draftYmd}
                                    onChange={(e) => {
                                      const next = String(e.target.value || '');
                                      setDueEditor((prev) => (prev ? { ...prev, draftYmd: next } : prev));
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape' || e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                                    }}
                                    onBlur={() => {
                                      const flowId = vm.flowId;
                                      const draftYmd = String(dueEditor.draftYmd || '');
                                      const initialYmd = String(dueEditor.initialYmd || '');
                                      setDueEditor(null);
                                      setSavingDueFlowId(flowId);
                                      void (async () => {
                                        try {
                                          if (draftYmd === initialYmd) return;
                                          await saveFlowTodoDueDate(flowId, draftYmd);
                                        } finally {
                                          setSavingDueFlowId((cur) => (cur === flowId ? null : cur));
                                        }
                                      })();
                                    }}
                                    style={{ padding: '4px 6px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                    aria-label="Edit due date"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className={dueBadgeClass}
                                    onClick={() => {
                                      setTodoEditor(null);
                                      const flowAny: any = vm.flow as any;
                                      const ft: any = Object.prototype.hasOwnProperty.call(flowAny || {}, 'flowTodo') ? (flowAny.flowTodo || {}) : null;
                                      const curYmd = ft ? String(ft?.dueDate || '').slice(0, 10) : '';
                                      const initialYmd =
                                        curYmd
                                        || (vm.todoDaysToDue !== null && !vm.needsDueDate ? ymdFromDaysToDueUtc(vm.todoDaysToDue) : '');
                                      setDueEditor({ flowId: vm.flowId, draftYmd: initialYmd, initialYmd });
                                    }}
                                    title="Click to edit due date"
                                    style={{ cursor: 'pointer', opacity: savingDueFlowId === vm.flowId ? 0.6 : 1 }}
                                  >
                                    {vm.needsDueDate ? 'No due date' : vm.todoDueLabel}
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="ops-board-chip ops-mono"
                                  onClick={() => openSquadStudentsModal(vm.squadId)}
                                  disabled={!vm.squadId}
                                  title="Manage students"
                                >
                                  {vm.studentCount} students
                                </button>

                                <button
                                  type="button"
                                  className="mini-btn ops-board-owner"
                                  onClick={(evt) => {
                                    const rect = (evt.currentTarget as HTMLElement).getBoundingClientRect();
                                    setAssigneePicker({ flowId: vm.flowId, anchorRect: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom } });
                                  }}
                                  title="Assign owner"
                                >
                                  {vm.ownerUser ? (
                                    <UserAvatar
                                      src={String((vm.ownerUser as any)?.headshotUrl || '') || undefined}
                                      username={String((vm.ownerUser as any)?.displayName || (vm.ownerUser as any)?.email || '') || undefined}
                                      size="small"
                                      variant="circular"
                                    />
                                  ) : (
                                    '—'
                                  )}
                                </button>
                              </div>

                              <div className="ops-board-card-todo">
                                {isEditingTodo ? (
                                  <textarea
                                    ref={todoTextareaRef}
                                    value={todoEditor.draft}
                                    rows={2}
                                    onChange={(e) => setTodoEditor((prev) => (prev ? { ...prev, draft: e.target.value } : prev))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') { e.currentTarget.blur(); return; }
                                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
                                    }}
                                    onBlur={() => {
                                      const flowId = vm.flowId;
                                      const draft = String(todoEditor.draft ?? '');
                                      const initial = String(todoEditor.initial ?? '');
                                      setTodoEditor(null);
                                      setSavingTodoFlowId(flowId);
                                      void (async () => {
                                        try {
                                          if (draft.trim() === initial.trim()) return;
                                          await saveFlowTodoNote(flowId, draft);
                                        } finally {
                                          setSavingTodoFlowId((cur) => (cur === flowId ? null : cur));
                                        }
                                      })();
                                    }}
                                    style={{
                                      width: '100%',
                                      fontSize: 12,
                                      lineHeight: 1.35,
                                      color: '#111827',
                                      fontWeight: 600,
                                      resize: 'vertical',
                                      padding: '6px 8px',
                                      borderRadius: 8,
                                      border: '1px solid #e5e7eb',
                                      background: 'white',
                                      minHeight: 44,
                                    }}
                                    placeholder="Add a TODO…"
                                    aria-label="Edit todo"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDueEditor(null);
                                      const current = safeString(vm.todo) ? String(vm.todo) : '';
                                      setTodoEditor({ flowId: vm.flowId, draft: current, initial: current });
                                    }}
                                    className="ops-board-todo-button"
                                    style={{ opacity: savingTodoFlowId === vm.flowId ? 0.6 : 1, color: safeString(vm.todo) ? '#374151' : '#9ca3af' }}
                                    title="Click to edit todo"
                                  >
                                    {safeString(vm.todo) ? vm.todoShort : 'Add a TODO…'}
                                  </button>
                                )}
                              </div>

                              {badgesToShow.length > 0 ? (
                                <div className="ops-board-card-badges">
                                  {badgesToShow.map((b) => (
                                    <span key={b.id} className={`ops-badge ops-badge-${b.tone}`}>{b.label}</span>
                                  ))}
                                </div>
                              ) : null}

                              <div className="ops-board-card-stage">
                                <select
                                  className="ops-select ops-board-stage-select"
                                  value={vm.pipelineStage}
                                  onChange={(e) => void moveFlowToStage(vm.flowId, e.target.value as OpsPipelineStageId, vm.pipelineStage)}
                                  aria-label="Change stage"
                                  title="Change stage"
                                >
                                  {OPS_PIPELINE_STAGES.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                  ))}
                                </select>
                              </div>
                              </>
                              ) : null}
                            </article>
                          );
                        })
                      )}
                    </div>
                  </section>
                );
              })}
          </div>
        </div>
      )}

      {assigneePicker ? (
        (() => {
          const width = 320;
          const estHeight = 360;
          const margin = 12;
          const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
          const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
          const rect = assigneePicker.anchorRect;
          let left = Math.min(Math.max(margin, rect.left), winW - width - margin);
          let top = rect.bottom + 8;
          if (top + estHeight + margin > winH) top = Math.max(margin, rect.top - estHeight - 8);

          const vm = vmByFlowId.get(assigneePicker.flowId) || null;
          const currentOwnerId = safeString(vm?.ownerUserId || '');

          return (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(17,24,39,0.08)' }}
              onMouseDown={() => setAssigneePicker(null)}
            >
              <div
                role="dialog"
                aria-label="Assign owner"
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  left,
                  top,
                  width,
                  maxWidth: `calc(100vw - ${margin * 2}px)`,
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  background: 'white',
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  padding: 12,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 900, color: '#111827' }}>Assign owner</div>
                  <button type="button" className="mini-btn" onClick={() => setAssigneePicker(null)}>Close</button>
                </div>

                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  {vm ? vm.squadTitle : 'Select a user'}
                </div>

                <div style={{ marginTop: 10, maxHeight: 320, overflow: 'auto', display: 'grid', gap: 6 }}>
                  <button
                    type="button"
                    className={currentOwnerId ? 'btn btn-secondary' : 'btn btn-primary'}
                    onClick={() => {
                      void (async () => {
                        await saveAssignee(assigneePicker.flowId, null);
                        setAssigneePicker(null);
                      })();
                    }}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    — Unassigned
                  </button>
                  {adminUsers.map((u) => {
                    const id = safeString((u as any)?.id);
                    if (!id) return null;
                    const label = safeString((u as any)?.displayName) || safeString((u as any)?.email) || id;
                    const selected = id === currentOwnerId;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={selected ? 'btn btn-primary' : 'btn btn-secondary'}
                        onClick={() => {
                          void (async () => {
                            await saveAssignee(assigneePicker.flowId, id);
                            setAssigneePicker(null);
                          })();
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start' }}
                      >
                        <UserAvatar
                          src={safeString((u as any)?.headshotUrl) || undefined}
                          username={label}
                          size="small"
                          variant="circular"
                        />
                        <span style={{ fontWeight: 800 }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()
      ) : null}

      <ManageSquadUsersModal
        isOpen={Boolean(manageStudentsSquad)}
        onClose={closeSquadStudentsModal}
        squad={manageStudentsSquad}
        onSquadUpdate={handleManageStudentsSquadUpdate}
      />
    </div>
  );
}

